import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import GetPendingApprovals from "../../utils/database/GetPendingApprovals";
import ApproveExtension from "../../utils/database/ApproveExtension";
import RejectExtension from "../../utils/database/RejectExtension";
import useUserDetails from "../../utils/auth/UserDetails";

const ApprovalQueuePage = () => {
  const userDetails = useUserDetails();
  const adminUserId = userDetails?.userId;

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actingId, setActingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [toast, setToast] = useState("");

  const refresh = useCallback(async () => {
    if (!adminUserId) return;
    setLoading(true);
    setError("");
    const res = await GetPendingApprovals(adminUserId);
    if (!res.success) {
      setError(res.error || "Failed to load pending approvals");
      setPending([]);
    } else {
      setPending(res.pending || []);
    }
    setLoading(false);
  }, [adminUserId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleApprove = async (extension) => {
    if (!adminUserId) return;
    setActingId(extension.id);
    const res = await ApproveExtension(adminUserId, extension.id);
    setActingId(null);
    if (!res.success) {
      setToast(`Approve failed: ${res.error}`);
      return;
    }
    setToast(`Approved "${extension.extensionName}"`);
    setPending((prev) => prev.filter((p) => p.id !== extension.id));
  };

  const openReject = (extension) => {
    setRejectTarget(extension);
    setRejectReason("");
  };

  const closeReject = () => {
    setRejectTarget(null);
    setRejectReason("");
  };

  const handleRejectConfirm = async () => {
    if (!adminUserId || !rejectTarget) return;
    if (!rejectReason.trim()) {
      setToast("Please provide a rejection reason");
      return;
    }
    setActingId(rejectTarget.id);
    const res = await RejectExtension(adminUserId, rejectTarget.id, rejectReason);
    setActingId(null);
    if (!res.success) {
      setToast(`Reject failed: ${res.error}`);
      return;
    }
    setToast(`Rejected "${rejectTarget.extensionName}"`);
    setPending((prev) => prev.filter((p) => p.id !== rejectTarget.id));
    closeReject();
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" sx={{ color: "#333366", fontWeight: "bold" }}>
          Approval Queue
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && pending.length === 0 && (
        <Card sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No extensions awaiting review.
          </Typography>
        </Card>
      )}

      {!loading && pending.map((ext) => (
        <Card key={ext.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box sx={{ flex: 1, pr: 2 }}>
                <Typography variant="h6" sx={{ color: "#333366" }}>
                  {ext.extensionName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted by {ext.submittedByName || ext.userId} {ext.submittedByEmail ? `(${ext.submittedByEmail})` : ""}
                  {ext.submittedAt ? ` · ${new Date(ext.submittedAt).toLocaleString()}` : ""}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: "#555" }}>
                  {ext.extensionDescription || <em>(no description)</em>}
                </Typography>

                <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {ext.category && <Chip size="small" label={ext.category} />}
                  {(ext.tools || []).map((t, i) => (
                    <Chip key={`t-${i}`} size="small" variant="outlined" label={t} />
                  ))}
                  {(ext.customTools || []).map((t, i) => (
                    <Chip
                      key={`c-${i}`}
                      size="small"
                      variant="outlined"
                      label={`${t.name} (${t.type || "custom"})`}
                      sx={{ bgcolor: "#f5f5fa" }}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 130 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => handleApprove(ext)}
                  disabled={actingId === ext.id}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<HighlightOffIcon />}
                  onClick={() => openReject(ext)}
                  disabled={actingId === ext.id}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Dialog open={Boolean(rejectTarget)} onClose={closeReject} fullWidth maxWidth="sm">
        <DialogTitle>Reject extension</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Rejecting <strong>{rejectTarget?.extensionName}</strong>. The owner will see this reason.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Why is this extension being rejected?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReject}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRejectConfirm}
            disabled={!rejectReason.trim() || actingId === rejectTarget?.id}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {toast && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            bgcolor: "#333366",
            color: "white",
            px: 2,
            py: 1,
            borderRadius: 1,
            zIndex: 1400,
          }}
          onClick={() => setToast("")}
        >
          {toast}
        </Box>
      )}
    </Box>
  );
};

export default ApprovalQueuePage;
